import type { DurationResult } from './types';

/**
 * Formats a standard date string (YYYY-MM-DD) into a more readable format.
 * @param dateString - Date in "YYYY-MM-DD" format.
 * @returns Formatted date string.
 */
export function formatDate(dateString: string): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Calculates the inclusive duration between two date strings.
 * @param startString - Start date in YYYY-MM-DD format.
 * @param endString - End date in YYYY-MM-DD format.
 * @returns Object with durationDays and durationText
 */
export function calculateDuration(startString: string, endString: string): DurationResult {
  const start = new Date(startString);
  const end = new Date(endString);

  // Add 1 day (24 hours) to include the return day in the duration count
  const durationMs = end.getTime() - start.getTime() + (1000 * 60 * 60 * 24);
  const durationDays = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
  const durationText = durationDays > 1 ? `${durationDays} days` : `${durationDays} day`;

  return { durationDays, durationText };
}

/**
 * Iterates through a date range and returns an array of "YYYY-MM-DD" strings.
 * @param startString - Start date in YYYY-MM-DD format.
 * @param endString - End date in YYYY-MM-DD format.
 * @returns Array of date strings
 */
export function getDaysInRange(startString: string, endString: string): string[] {
  const dates: string[] = [];
  // Parse dates and normalize to prevent timezone issues during iteration
  const start = new Date(startString);
  const end = new Date(endString);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  let currentDate = new Date(start);
  while (currentDate.getTime() <= end.getTime()) {
    dates.push(currentDate.toISOString().slice(0, 10));
    // Move to the next day
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
}

/**
 * Calculates a map of dates to the number of concurrent holidays (overlap count).
 * @param holidays - Array of holiday objects from Firestore.
 * @returns Map where key is "YYYY-MM-DD" date string and value is the count of people absent.
 */
export function calculateOverlaps(holidays: Array<{ startDate: string; endDate: string }>): Map<string, number> {
  const dateCountMap = new Map<string, number>();
  holidays.forEach(holiday => {
    const days = getDaysInRange(holiday.startDate, holiday.endDate);
    days.forEach(dateKey => {
      dateCountMap.set(dateKey, (dateCountMap.get(dateKey) || 0) + 1);
    });
  });
  return dateCountMap;
}

